import { getCurrentInstance } from 'vue'

import { useConversion } from '@/composables/core/useConversion'
import { getAnalyticsPageTitleContext, resolveAnalyticsPageTitle } from '@/utils/analyticsPageTitle'
import { captureConversionAttribution, convCookies, readLaunchAttribution } from '@/utils/conversionCookies'
import { convertEventName, isEventBlocked } from '@/utils/convertEventName'
import { normalizeCurrency } from '@/utils/currencyNormalize'
import { resolveConversionDispatch, validateEventPayload, enrichEventPayload, SECOND_ARG_EVENTS } from '@/utils/eventContract'
import { createPageContextTracker } from '@/utils/pageContext'
import { createPwaOpenCapture, getDisplayMode, resolvePwaSrc, safeWindowStorage, scheduleIdle } from '@/utils/pwaLaunch'
import { resolveReferralKpix } from '@/utils/referral'
import applyUmamiVendorRename from '@/utils/umamiVendorPayload'

export default defineNuxtPlugin((nuxtApp) => {
  const { $bridge, $gtag, $fb, $kwaiq, $tiktok, $umami } = nuxtApp
  const config = useRuntimeConfig()
  const isDev = config.public?.runningEnv === 'dev' || config.public?.runningEnv === 'qa'

  // 順序重要:先擷取啟動 URL 的 click 歸因,啟動快照才能反映「啟動當下」的 conv_* 與 token
  captureConversionAttribution(window.location.search)

  const launchAttribution = {
    ...readLaunchAttribution(),
    eventTime: Math.floor(Date.now() / 1000),
    hadTokenAtLaunch: !!useAuthToken().token.value,
  }

  const { sendConversionEvent } = useConversion()
  const analyticsPageTitleContext = getAnalyticsPageTitleContext(nuxtApp)
  const pageContextTracker = createPageContextTracker()

  useRouter().afterEach((to) => {
    captureConversionAttribution(to.fullPath)
  })

  // 全域中繼資料（會話內固定）：每個事件由 dispatcher 自動補入。詳見 §1.5 規範。
  // 缺 version 時用明確 sentinel `'unknown'`，避免送出無意義的 `'v'` 字串污染 dashboard。
  const GLOBAL_META = {
    pwa_version: config.public?.appVersion ? `v${config.public.appVersion}` : 'unknown',
    display_mode: getDisplayMode(),
  }

  /**
   * 派發單一事件給所有 tracker。
   * @param {Object} obj           事件 payload，必含 event 欄位
   * @param {Object|null} eventID  FB Pixel 去重用 wrapper，shape: { eventID: string }
   *                                僅 SECOND_ARG_EVENTS（purchase / first_deposit / sign_up_success）需傳。
   * @param {Object|null} opts     S2S conversion 選項，見 utils/eventContract.js resolveConversionDispatch
   */
  function push(obj, eventID = null, opts = null) {
    if (!obj || !obj.event) {
      if (isDev) console.warn('[analytics] push called with invalid payload', obj)
      return
    }

    const userInfoStore = nuxtApp.$pinia?._s.get('userInfo')
    const profile = userInfoStore?.userInfo || {}
    // channel / subChannel 已棄用（2026-Q2），不再隨事件發送。
    const user_properties = {
      level: profile.level || 0,
      strategy: profile.strategy || '',
      scope: profile.scope || '',
      topup: profile.topup || 0,
      verification_status: profile.verification_status || 0,
      uidSite: profile.uid_site || '',
      user_tag_RFM: profile.user_tag || '',
      agent_id: profile.agent_id || '',
    }

    // Auto-enrichment（pure function，便於測試）：補 page_title、component_name、全域中繼
    // （pwa_version, display_mode）。channel / sub_channel 已於 2026-Q2 全面棄用，
    // 不再由 callsite 傳入；contract validation (utils/eventContract.js) 主動 reject
    // 殘留的舊欄位。
    //
    // component_name 解析順序：
    //   1. push 呼叫當下的 Vue instance（onMounted / onBeforeUnmount 等 lifecycle 內可取到）
    //   2. 若為 BaseModal/Modal，改用 mainStore.getModal 推導外層 modal 名稱（ModalMission -> Mission）
    //   3. 空字串
    const instance = getCurrentInstance()
    const mainStore = nuxtApp.$pinia?._s.get('main')
    const resolvedInstanceName = instance?.type?.__name || instance?.type?.name || ''
    const isBaseModalInstance = ['BaseModal', 'Modal'].includes(resolvedInstanceName)
    const modalName = mainStore?.getModal
    const modalComponentName = typeof modalName === 'string' ? modalName.replace(/^Modal/, '') : ''
    const resolvedComponentName = (isBaseModalInstance ? '' : resolvedInstanceName) || modalComponentName || ''
    const documentTitle = typeof document !== 'undefined' ? document.title : ''
    const resolvedPageContext = pageContextTracker.resolve(
      {
        ...obj,
        page_title: resolveAnalyticsPageTitle(obj, analyticsPageTitleContext.current(), documentTitle),
      },
      documentTitle
    )
    const callTimeMeta = {
      ...GLOBAL_META,
      page_title: resolvedPageContext.page_title,
      prev_page_title: resolvedPageContext.prev_page_title,
      component_name: resolvedComponentName,
    }
    const enriched = enrichEventPayload(obj, callTimeMeta)
    const objData = { ...enriched, user_id: user_properties.uidSite, user_properties }

    // Contract validation：只在 dev/qa console.warn，不阻擋發送。
    // 必須在 page context 補值之後跑，避免 error_event 誤警告 missing page_title。
    if (isDev) {
      const result = validateEventPayload(obj.event, objData)
      if (!result.ok) {
        console.warn(`[analytics] contract violation: ${result.reason}`, objData)
      }
      // SECOND_ARG_EVENTS：必須帶第二個參數含 eventID（FB Pixel 去重）
      if (SECOND_ARG_EVENTS.has(obj.event) && !eventID?.eventID) {
        console.warn(`[analytics] contract violation: event "${obj.event}" requires 2nd arg { eventID }`, obj)
      }
    }

    // 幣別正規化：送 GA4 / FB / TikTok / Kwai 前把非 ISO 4217 / 千單位 currency 轉成
    // tracker 認得的 ISO 碼。Bridge / Native 端仍會收到「轉換前」的 objData（C7 deferred）。
    // 規則 + 表格：utils/currencyNormalize.js
    const specialData = normalizeCurrency(objData)

    // Kwaiq 追蹤
    const kpix = resolveReferralKpix()
    if ($kwaiq?.instance && kpix) {
      $kwaiq.instance(kpix)?.track(convertEventName(obj.event, 'kwai'), specialData)
    }

    // Google Analytics 追蹤
    if ($gtag) {
      $gtag('event', obj.event, specialData)
    }

    // FB Pixel 追蹤
    if ($fb && typeof $fb.query === 'function') {
      let fbData = { ...specialData }
      const fbEventName = convertEventName(obj.event, 'facebook')

      // 只有當事件名稱有效時才發送 (過濾 null 或 undefined)
      if (fbEventName !== null && fbEventName !== undefined) {
        // [特定追蹤方法/特定事件] 當是註冊成功事件時 不需要傳遞幣別和金額
        if (fbEventName === 'CompleteRegistration') {
          const { currency: _currency, value: _value, ...restData } = fbData
          fbData = restData
        }
        $fb.query('trackCustom', fbEventName, fbData, eventID)
      }
    }

    // TikTok Pixel 追蹤
    if ($tiktok && $tiktok.isLoaded && $tiktok.isLoaded()) {
      const tiktokEvent = convertEventName(obj.event, 'tiktok')
      if (tiktokEvent) {
        $tiktok.track(tiktokEvent, specialData)
      }
    }

    // Umami 追蹤
    if ($umami && $umami.enabled && !isEventBlocked(obj.event, 'umami')) {
      const umamiEvent = convertEventName(obj.event, 'umami')
      if (umamiEvent) {
        const { event: _evt, user_properties: _up, ...umamiPayload } = specialData
        // purchase / first_deposit 需走 Umami Revenue API（value → revenue，搭配 currency）。
        // 詳見 utils/umamiVendorPayload.js 與 docs/standards/event-tracking-spec.md §4.3。
        $umami.track(umamiEvent, applyUmamiVendorRename(obj.event, umamiPayload, { isDev }))
      }
    }

    if ($bridge?.available) {
      $bridge.callHandler('pushEvents', objData, () => {
        console.log('[Bridge] Push Events')
      })
    }
    // 部分瀏覽器 / WebView 會注入存在但 postMessage 非函式的 window.jsBridge，
    // 僅用 ?. 仍會在呼叫時拋 TypeError，需以能力偵測確認可呼叫後再使用
    if (typeof window !== 'undefined') {
      const jsBridgePayload = JSON.stringify(objData || {})
      try {
        if (typeof window.jsBridge?.postMessage === 'function') {
          window.jsBridge.postMessage(obj.event, jsBridgePayload)
        }
      } catch (e) {
        // Android @JavascriptInterface object may be GC'd; silently ignore
      }
    }

    // S2S conversion 分支:任何例外不得影響上方既有通道
    try {
      const conversionDispatch = resolveConversionDispatch({
        event: obj.event,
        isDevLike: isDev,
        secondArg: eventID,
        opts,
        cookieTraceId: convCookies.get('conv_trace_id'),
      })
      if (conversionDispatch) {
        sendConversionEvent({
          event: obj.event,
          payload: specialData,
          eventId: conversionDispatch.eventId,
          attribution: conversionDispatch.attribution,
        })
      }
    } catch (error) {
      if (isDev) console.warn('[Conversion] dispatch failed:', error)
    }

    if (isDev) {
      console.info(
        `%c[Analytics][DEFAULT: ${obj.event} | KWAI: ${convertEventName(obj.event, 'kwai')} | FB: ${convertEventName(
          obj.event,
          'facebook'
        )} | TikTok: ${convertEventName(obj.event, 'tiktok')}]`,
        'background-color: #fff000; color: #000000;'
      )
    }
  }

  const pwaSrc = resolvePwaSrc({ search: window.location.search, displayMode: getDisplayMode() })
  if (pwaSrc) {
    scheduleIdle(
      createPwaOpenCapture({
        push,
        src: pwaSrc,
        launchAttribution,
        sessionStorage: safeWindowStorage(() => window.sessionStorage),
        localStorage: safeWindowStorage(() => window.localStorage),
      }),
      window
    )
  }

  const analytics = {
    push,
  }

  return {
    provide: {
      analytics,
    },
  }
})
